import { calculateDuration } from "../utils";
import { isDefined } from "@clipboard-health/util-ts";
import { differenceInMinutes } from "date-fns";
import meta from "src/utils/meta";
import { formatLunchBreakWarning } from "@src/appV2/Shifts/utils/formatLunchBreakWarning";
import {
  resolveShiftBreakDurationInMinutes,
  resolveShiftBreakRule,
  type WorkplaceBreakPolicy,
} from "@src/appV2/Shifts/utils/resolve-shift-break-rule";
import { formatTime } from "../utils";

const { maxShiftDuration, minShiftDuration } = meta();

export interface ValidationError {
  shiftType: string;
  error: string;
}

export interface ShiftWarning {
  shiftType: string;
  warning: string;
}

export interface OverlapWarning {
  shiftType1: string;
  shiftType2: string;
  warning: string;
}

/**
 * Validates if shift duration is within acceptable bounds
 */
export function validateShiftDuration(
  startTime: Date | undefined,
  endTime: Date | undefined
): string | null {
  if (!startTime || !endTime) {
    return null;
  }

  // Check if start time is before end time
  if (startTime >= endTime) {
    return "Start time must be before end time";
  }

  const totalShiftHours = calculateDuration(startTime, endTime);
  if (!isDefined(totalShiftHours)) {
    return "Invalid time range";
  }

  const durationInHours = totalShiftHours.hours + totalShiftHours.minutes / 60;

  if (durationInHours > maxShiftDuration) {
    return `Shift duration cannot exceed ${maxShiftDuration} hours`;
  }

  if (durationInHours < minShiftDuration) {
    return `Shift duration must be at least ${minShiftDuration} hours`;
  }

  return null;
}

/**
 * Validates if shift duration is within acceptable bounds (returns boolean for schema validation)
 * This function is designed to be used with Zod schema validation
 */
export function isShiftDurationValid(startTime: Date, endTime: Date): boolean {
  const totalShiftHours = calculateDuration(startTime, endTime);
  if (!isDefined(totalShiftHours)) {
    return false;
  }

  const durationInHours = totalShiftHours.hours + totalShiftHours.minutes / 60;
  return durationInHours >= minShiftDuration && durationInHours <= maxShiftDuration;
}

/**
 * Gets warnings for shifts that require lunch breaks, using the workplace's
 * tier-aware break policy to render the resolved tier's threshold and length.
 */
export function getShiftWarnings(
  shifts: Array<{ startTime?: Date; endTime?: Date; shiftType: string }>,
  breakPolicies?: WorkplaceBreakPolicy
): ShiftWarning[] {
  const warnings: ShiftWarning[] = [];

  shifts.forEach(({ startTime, endTime, shiftType }) => {
    if (!startTime || !endTime) {
      return;
    }

    const durationInMinutes = differenceInMinutes(endTime, startTime);
    const rule = resolveShiftBreakRule({ policy: breakPolicies, durationInMinutes });
    if (!isDefined(rule)) {
      return;
    }

    warnings.push({ shiftType, warning: formatLunchBreakWarning(rule) });
  });

  return warnings;
}

/**
 * Checks for overlapping shifts between different shift types
 */
export function getOverlapWarnings(
  shifts: Array<{ startTime?: Date; endTime?: Date; shiftType: string }>
): OverlapWarning[] {
  const warnings: OverlapWarning[] = [];

  // Check for overlaps between consecutive shifts
  for (let i = 0; i < shifts.length - 1; i++) {
    const current = shifts[i];
    const next = shifts[i + 1];

    if (current.startTime && current.endTime && next.startTime && next.endTime) {
      // Check if shifts overlap (start1 < end2 && start2 < end1)
      if (current.startTime < next.endTime && next.startTime < current.endTime) {
        warnings.push({
          shiftType1: current.shiftType,
          shiftType2: next.shiftType,
          warning: `${current.shiftType} and ${next.shiftType} shifts overlap`,
        });
      }
    }
  }

  // Special case: NOC vs AM (next day) - NOC shifts typically end early morning
  const nocShift = shifts.find((s) => s.shiftType === "NOC");
  const amShift = shifts.find((s) => s.shiftType === "AM");

  if (nocShift?.startTime && nocShift?.endTime && amShift?.startTime && amShift?.endTime) {
    // For NOC vs AM overlap, we need to handle shifts that span across midnight
    // NOC shift typically ends early morning (12 AM - 8 AM)
    // AM shift typically starts morning (6 AM - 12 PM)

    // Normalize NOC end time to the same day as AM start time for comparison
    const normalizedNocEnd = new Date(amShift.startTime);
    normalizedNocEnd.setHours(nocShift.endTime.getHours(), nocShift.endTime.getMinutes());

    // Only warn if there's an actual overlap (normalized NOC end is after AM start)
    if (normalizedNocEnd > amShift.startTime) {
      warnings.push({
        shiftType1: "NOC",
        shiftType2: "AM",
        warning: "NOC and AM shifts overlap (NOC shift extends into AM shift time)",
      });
    }
  }

  return warnings;
}

/**
 * Validates multiple shifts and returns all validation errors
 */
export function validateMultipleShifts(
  shifts: Array<{ startTime?: Date; endTime?: Date; shiftType: string }>
): ValidationError[] {
  return shifts
    .map(({ startTime, endTime, shiftType }) => ({
      shiftType,
      error: validateShiftDuration(startTime, endTime),
    }))
    .filter(({ error }) => error !== null) as ValidationError[];
}

/**
 * Gets the total duration of a shift in hours
 */
export function getShiftDurationInHours(
  startTime: Date | undefined,
  endTime: Date | undefined
): number {
  if (!startTime || !endTime) {
    return 0;
  }

  const totalShiftHours = calculateDuration(startTime, endTime);
  if (!isDefined(totalShiftHours)) {
    return 0;
  }

  return totalShiftHours.hours + totalShiftHours.minutes / 60;
}

/**
 * Format duration with break information based on shift duration and workplace break policy.
 */
export function formatDurationWithBreak(
  startTime: Date,
  endTime: Date,
  breakPolicies?: WorkplaceBreakPolicy
): string {
  const totalDuration = calculateDuration(startTime, endTime);
  if (!totalDuration) {
    return "Invalid time range";
  }

  const totalDurationInMinutes = totalDuration.hours * 60 + totalDuration.minutes;
  if (Number.isNaN(totalDurationInMinutes)) {
    return formatTime(totalDuration);
  }

  const breakDurationInMinutes = resolveShiftBreakDurationInMinutes({
    policy: breakPolicies,
    durationInMinutes: totalDurationInMinutes,
  });

  if (breakDurationInMinutes <= 0) {
    return formatTime(totalDuration);
  }

  const breakDurationTimeObject = {
    hours: Math.floor(breakDurationInMinutes / 60),
    minutes: breakDurationInMinutes % 60,
  };

  const workingDurationInMinutes = totalDurationInMinutes - breakDurationInMinutes;
  const workingDurationTimeObject = {
    hours: Math.floor(workingDurationInMinutes / 60),
    minutes: workingDurationInMinutes % 60,
  };

  return `${formatTime(workingDurationTimeObject)} + ${formatTime(breakDurationTimeObject)} break`;
}
